//내 프로필 메인 화면 전체 렌더링
import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import hututuFrontImg from "../../assets/images/hututi_front.png";
import hututuSideImg from "../../assets/images/hututi_side.png";
import "./Profile.css";

const hututuImg = Math.random() < 0.5 ? hututuFrontImg : hututuSideImg;

function Profile() {
  const [profile, setProfile] = useState(null);
  const [myClubs, setMyClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [showMyComments, setShowMyComments] = useState(false);
  const [myPosts, setMyPosts] = useState([]);
  const [myComments, setMyComments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProfile() {
      // 세션 확인: 비로그인 상태이면 홈으로 리다이렉트
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/"); return; }

      // 세션의 이메일로 users 테이블에서 상세 프로필 정보 조회
      const { data: userData, error } = await supabase
        .from("users").select("*").eq("email", session.user.email).single();

      if (error) {
        console.error("프로필 로딩 오류:", error);
      } else {
        setProfile(userData);

        // 가입한 동아리 조회: club_members → clubs 순서의 2단계 쿼리
        // 1단계: club_members에서 로그인 유저의 club_id 목록과 역할(role) 가져오기
        const { data: memberData } = await supabase
          .from("club_members").select("club_id, role").eq("user_id", userData.id);

        if (memberData && memberData.length > 0) {
          const clubIds = memberData.map((m) => m.club_id);

          // 2단계: 수집한 club_id 배열로 clubs 테이블에서 상세 정보 한 번에 조회 (in 연산자)
          const { data: clubsData } = await supabase
            .from("clubs").select("id, name, image_url, category, recruitment_status").in("id", clubIds);

          if (clubsData) {
            // 각 동아리에 memberData에서 역할(role)을 매핑해 하나의 객체로 결합
            setMyClubs(clubsData.map((club) => ({
              clubs: club,
              role: memberData.find((m) => m.club_id === club.id)?.role,
            })));
          }
        }
      }
      setLoading(false);
    }
    fetchProfile();
  }, [navigate]);

  async function handleOpenMyPosts() {
    const { data } = await supabase
      .from("lost_items").select("id, title, item_type, location, item_date")
      .eq("user_id", profile.id).order("created_at", { ascending: false });
    setMyPosts(data || []);
    setShowMyPosts(true);
  }

  async function handleOpenMyComments() {
    const { data } = await supabase
      .from("comments").select("id, content, created_at, lost_item_id, lost_items(title)")
      .eq("user_id", profile.id).order("created_at", { ascending: false });
    setMyComments(data || []);
    setShowMyComments(true);
  }

  function handleEditStart() {
    setEditData({
      login_id: profile.login_id ?? "",
      name: profile.name ?? "",
      department: profile.department ?? "",
      grade: profile.grade ?? "",
      phone: profile.phone ?? "",
    });
    setIsEditing(true);
  }

  function handleEditCancel() {
    setIsEditing(false);
    setEditData({});
  }

  async function handleEditSave() {
    setSaving(true);
    if (editData.login_id !== profile.login_id) {
      const { data: existing } = await supabase
        .from("users").select("id").eq("login_id", editData.login_id).single();
      if (existing) {
        alert("이미 사용 중인 아이디예요.");
        setSaving(false);
        return;
      }
    }
    const { error } = await supabase.from("users").update({
      login_id: editData.login_id,
      name: editData.name,
      department: editData.department,
      grade: editData.grade !== "" ? Number(editData.grade) : null,
      phone: editData.phone,
    }).eq("id", profile.id);

    if (error) {
      alert("저장 실패: " + error.message);
    } else {
      setProfile({ ...profile, ...editData });
      setIsEditing(false);
      alert("저장됐습니다!");
    }
    setSaving(false);
  }

  if (loading) return <div className="profile-loading">로딩 중...</div>;
  if (!profile) return <div className="profile-loading">프로필 정보를 불러올 수 없습니다.</div>;

  const infoRows = [
    { label: "아이디",   field: "login_id",      editable: true,  type: "text" },
    { label: "이메일",   field: "email",          editable: false },
    { label: "이름",     field: "name",           editable: true,  type: "text" },
    { label: "학번",     field: "student_number", editable: false },
    { label: "학과",     field: "department",     editable: true,  type: "text" },
    { label: "학년",     field: "grade",          editable: true,  type: "select", options: ["1","2","3","4"] },
    { label: "성별",     field: "gender",         editable: false },
    { label: "휴대폰",   field: "phone",          editable: true,  type: "text" },
    { label: "입학년도", field: "admission_year", editable: false },
    { label: "생년월일", field: "birth_date",     editable: false },
  ];

  function displayValue(field, value) {
    if (field === "grade" && value) return `${value}학년`;
    if (field === "admission_year" && value) return `${value}년`;
    return value ?? "-";
  }

  return (
    <>
      <div className="profile-container">
        {/* 헤더 카드 */}
        <div className="profile-header-card">
          <div className="profile-avatar">
            <img src={hututuImg} alt="프로필" className="profile-avatar-img" />
          </div>
          <div className="profile-header-info">
            <h2 className="profile-name">{profile.name}</h2>
            <p className="profile-sub">{profile.department}</p>
          </div>
        </div>

        {/* 관리자 버튼: users 테이블의 role이 "admin"인 경우에만 조건부 렌더링 */}
        {profile.role === "admin" && (
          <button className="profile-admin-btn" onClick={() => navigate("/admin")}>
            관리자 페이지로 이동하기
          </button>
        )}

        {/* 내 정보 */}
        <div className="profile-card">
          <div className="profile-card-title-row">
            <h3 className="profile-card-title">내 정보</h3>
            {!isEditing ? (
              <button className="profile-edit-btn" onClick={handleEditStart}>수정</button>
            ) : (
              <div className="profile-edit-actions">
                <button className="profile-save-btn" onClick={handleEditSave} disabled={saving}>
                  {saving ? "저장 중..." : "저장"}
                </button>
                <button className="profile-cancel-btn" onClick={handleEditCancel}>취소</button>
              </div>
            )}
          </div>
          <div className="profile-info-list">
            {infoRows.map(({ label, field, editable, type, options }) => (
              <div className="profile-info-row" key={field}>
                <span className="profile-info-label">{label}</span>
                {isEditing && editable ? (
                  type === "select" ? (
                    <select className="profile-edit-select" value={editData[field]}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}>
                      <option value="">선택</option>
                      {options.map((opt) => (
                        <option key={opt} value={opt}>{opt}학년</option>
                      ))}
                    </select>
                  ) : (
                    <input className="profile-edit-input" type="text" value={editData[field]}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })} />
                  )
                ) : (
                  <span className="profile-info-value">{displayValue(field, profile[field])}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 내가 쓴 게시글 / 댓글 바로가기 */}
        <div className="profile-shortcut-row">
          <button className="profile-shortcut-btn" onClick={handleOpenMyPosts}>
            <span className="profile-shortcut-icon">📝</span>
            <span>내가 쓴 게시글</span>
          </button>
          <button className="profile-shortcut-btn" onClick={handleOpenMyComments}>
            <span className="profile-shortcut-icon">💬</span>
            <span>내가 쓴 댓글</span>
          </button>
        </div>

        {/* 내 동아리 */}
        <div className="profile-card profile-club-card">
          <h3 className="profile-card-title">
            내 동아리
            <span className="profile-club-count">{myClubs.length}</span>
          </h3>
          {myClubs.length === 0 ? (
            <div className="profile-club-empty">
              <span className="profile-club-empty-icon">🎯</span>
              <p>아직 가입한 동아리가 없어요</p>
            </div>
          ) : (
            <div className="profile-club-list">
              {myClubs.map(({ role, clubs: club }) => (
                <div className="profile-club-item" key={club.id} onClick={() => navigate(`/clubs/${club.id}`)}>
                  <div className="profile-club-img-wrap">
                    {club.image_url ? (
                      <img src={club.image_url} alt={club.name} className="profile-club-img" />
                    ) : (
                      <div className="profile-club-img-placeholder">{club.name?.charAt(0)}</div>
                    )}
                  </div>
                  <div className="profile-club-info">
                    <span className="profile-club-name">{club.name}</span>
                    <span className="profile-club-category">
                      {Array.isArray(club.category)
                        ? club.category.join(', ')
                        : (() => { try { return JSON.parse(club.category).join(', '); } catch { return club.category; } })()}
                    </span>
                  </div>
                  <div className="profile-club-badges">
                    <span className={`profile-club-role ${role === "leader" ? "role-leader" : "role-member"}`}>
                      {role === "leader" ? "동아리장" : "멤버"}
                    </span>
                    <span className={`profile-club-status ${club.recruitment_status === "모집 중" ? "status-open" : "status-closed"}`}>
                      {club.recruitment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 내가 쓴 게시글 모달 */}
      {showMyPosts && (
        <div className="profile-modal-overlay" onClick={() => setShowMyPosts(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>내가 쓴 게시글</h3>
              <button className="profile-modal-close" onClick={() => setShowMyPosts(false)}>✕</button>
            </div>
            {myPosts.length === 0 ? (
              <p className="profile-modal-empty">작성한 게시글이 없어요</p>
            ) : (
              <ul className="profile-modal-list">
                {myPosts.map((post) => (
                  <li key={post.id} className="profile-modal-item"
                    onClick={() => { setShowMyPosts(false); navigate("/lost", { state: { openItemId: post.id } }); }}>
                    <div className="profile-modal-item-title">{post.title}</div>
                    <div className="profile-modal-item-sub">
                      <span className={`profile-modal-badge ${post.item_type === "분실" ? "badge-lost" : "badge-found"}`}>
                        {post.item_type}
                      </span>
                      <span>{post.location}</span>
                      <span>{post.item_date}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* 내가 쓴 댓글 모달 */}
      {showMyComments && (
        <div className="profile-modal-overlay" onClick={() => setShowMyComments(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>내가 쓴 댓글</h3>
              <button className="profile-modal-close" onClick={() => setShowMyComments(false)}>✕</button>
            </div>
            {myComments.length === 0 ? (
              <p className="profile-modal-empty">작성한 댓글이 없어요</p>
            ) : (
              <ul className="profile-modal-list">
                {myComments.map((comment) => (
                  <li key={comment.id} className="profile-modal-item"
                    onClick={() => { setShowMyComments(false); navigate("/lost", { state: { openItemId: comment.lost_item_id } }); }}>
                    <div className="profile-modal-item-title">{comment.content}</div>
                    <div className="profile-modal-item-sub">
                      <span>📌 {comment.lost_items?.title}</span>
                      <span>{new Date(comment.created_at).toLocaleDateString("ko-KR")}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Profile;
